// Canonical location helpers — pure functions shared by create/edit flows.

export const NAMED_OBJECT_MODE_CANONICAL = "canonical";
export const NAMED_OBJECT_MODE_MANUAL = "manual";

export const LOCAL_AREA_MAX_LENGTH = 128;
export const MANUAL_ADDRESS_OBJECT_MAX_LENGTH = 255;
export const BUILDING_NUMBER_MAX_LENGTH = 32;

export const ADDRESS_OBJECT_KIND_LABELS = {
  STREET: "вулиця",
  LANE: "провулок",
  SQUARE: "площа",
  PROSPECT: "проспект",
  BOULEVARD: "бульвар",
  DESCENT: "узвіз",
  EMBANKMENT: "набережна",
  HIGHWAY: "шосе",
  DEAD_END: "тупик",
  MICRODISTRICT: "мікрорайон",
  RESIDENTIAL_MASSIF: "житловий масив",
  TRACT: "урочище",
  OTHER: "інше",
};

export const EMPTY_LOCATION_GEO_STATE = {
  selectedRegion: null,
  selectedSettlement: null,
  canonicalSelectionDirty: false,
  structuredIdentityDirty: false,
  settlement_unit: null,
  local_area: "",
  address_object: null,
  manual_address_object: "",
  building_number: "",
  namedObjectMode: NAMED_OBJECT_MODE_CANONICAL,
  legacyUnresolvedAddress: "",
  legacyAddressResolved: true,
  legacyCompatibilityCopy: false,
};

function trimText(value) {
  return String(value ?? "").trim();
}

function settlementCodeOf(unit) {
  return unit && unit.code ? unit.code : null;
}

function addressObjectIdOf(addressObject) {
  if (!addressObject || addressObject.id == null || addressObject.id === "") {
    return null;
  }
  return addressObject.id;
}

export function findRootRegionFromPath(path) {
  if (!Array.isArray(path)) return null;
  return path.find((item) => item.category === "O" || item.category === "K") ?? null;
}

export function syncLegacyFromCanonical(selectedRegion, selectedSettlement) {
  return {
    country: "Україна",
    region: selectedRegion?.name ?? "",
    city: selectedSettlement?.name ?? "",
  };
}

export function isC1LegacyCompatibilityCopy({
  address,
  address_object: addressObject,
  manual_address_object: manualAddressObject,
  building_number: buildingNumber,
}) {
  const addressText = trimText(address);
  const manualText = trimText(manualAddressObject);
  return (
    addressObjectIdOf(addressObject) == null
    && addressText !== ""
    && manualText !== ""
    && manualText === addressText
    && trimText(buildingNumber) === ""
  );
}

export function detectUnresolvedLegacyAddress({
  address,
  address_object: addressObject,
  manual_address_object: manualAddressObject,
  building_number: buildingNumber,
}) {
  if (trimText(address) === "") return false;
  if (addressObjectIdOf(addressObject) != null) return false;
  if (trimText(manualAddressObject) === "") return true;
  return isC1LegacyCompatibilityCopy({
    address,
    address_object: addressObject,
    manual_address_object: manualAddressObject,
    building_number: buildingNumber,
  });
}

export function hasUnresolvedLegacyAddress(location) {
  if (!location) return false;
  if (location.legacyAddressResolved) return false;
  return trimText(location.legacyUnresolvedAddress) !== "";
}

export function hasInactiveLinkedAddressObject(location) {
  if (!location?.address_object) return false;
  if (location.namedObjectMode === NAMED_OBJECT_MODE_MANUAL) return false;
  return location.address_object.is_active === false;
}

export function enrichLocationFromApi(apiLocation) {
  const latitude =
    apiLocation?.latitude === null || apiLocation?.latitude === undefined
      ? ""
      : apiLocation.latitude;
  const longitude =
    apiLocation?.longitude === null || apiLocation?.longitude === undefined
      ? ""
      : apiLocation.longitude;

  const settlement_unit = apiLocation?.settlement_unit ?? null;
  const selectedSettlement = settlement_unit;
  const selectedRegion = settlement_unit
    ? findRootRegionFromPath(settlement_unit.path)
    : null;
  const address_object = apiLocation?.address_object ?? null;
  const manual_address_object = apiLocation?.manual_address_object ?? "";
  const local_area = apiLocation?.local_area ?? "";
  const building_number = apiLocation?.building_number ?? "";
  const namedObjectMode = (
    !trimText(manual_address_object)
      ? NAMED_OBJECT_MODE_CANONICAL
      : NAMED_OBJECT_MODE_MANUAL
  );

  return {
    latitude,
    longitude,
    id: apiLocation?.id ?? null,
    ...EMPTY_LOCATION_GEO_STATE,
    settlement_unit,
    selectedRegion,
    selectedSettlement,
    local_area,
    address_object,
    manual_address_object,
    building_number,
    namedObjectMode,
    legacyUnresolvedAddress: "",
    legacyAddressResolved: true,
    legacyCompatibilityCopy: false,
  };
}

export function isLegacyOnlyLocation(location) {
  if (!location) return false;
  if (location.settlement_unit) return false;
  if (location.selectedRegion || location.selectedSettlement) return false;
  return hasPersistedLocation(location);
}

/** True when location row exists in backend (API id), not an unsaved form shell. */
export function hasPersistedLocation(location) {
  if (!location) return false;
  const id = location.id ?? location.pk;
  return id != null && id !== "";
}

export function needsLocationUpgrade(location) {
  if (!location) return false;
  if (!hasPersistedLocation(location)) return false;
  return !location.settlement_unit;
}

export function buildEditLocationHref(propertyId) {
  return `/realty/${propertyId}/edit#sec-location`;
}

export function extractLegacyLocationReference(location) {
  if (!location) {
    return { region: "", city: "" };
  }
  return {
    region: trimText(location.region),
    city: trimText(location.city),
  };
}

export function clearNamedAddressDependents(location) {
  return {
    ...location,
    local_area: "",
    address_object: null,
    manual_address_object: "",
    building_number: "",
    namedObjectMode: NAMED_OBJECT_MODE_CANONICAL,
    structuredIdentityDirty: true,
  };
}

export function applyRegionSelection(location, region) {
  const previousSettlementCode = settlementCodeOf(location.selectedSettlement);
  const next = {
    ...location,
    selectedRegion: region,
    canonicalSelectionDirty: true,
    selectedSettlement: null,
  };

  if (!region) {
    return previousSettlementCode
      ? clearNamedAddressDependents(next)
      : next;
  }

  if (region.category === "K") {
    const withSettlement = {
      ...next,
      selectedSettlement: region,
    };
    if (previousSettlementCode !== region.code) {
      return clearNamedAddressDependents(withSettlement);
    }
    return withSettlement;
  }

  if (previousSettlementCode) {
    return clearNamedAddressDependents(next);
  }
  return next;
}

export function applySettlementSelection(location, settlement) {
  if (!location.selectedRegion || !settlement) {
    return location;
  }
  const previousSettlementCode = settlementCodeOf(location.selectedSettlement);
  const next = {
    ...location,
    selectedSettlement: settlement,
    canonicalSelectionDirty: true,
  };
  if (previousSettlementCode !== settlement.code) {
    return clearNamedAddressDependents(next);
  }
  return next;
}

export function applyLocalAreaChange(location, localArea) {
  return {
    ...location,
    local_area: localArea,
    structuredIdentityDirty: true,
  };
}

export function applyAddressObjectSelection(location, addressObject) {
  const previousId = addressObjectIdOf(location.address_object);
  const nextId = addressObjectIdOf(addressObject);
  const switchedFromManual =
    location.namedObjectMode === NAMED_OBJECT_MODE_MANUAL;
  const next = {
    ...location,
    address_object: addressObject,
    manual_address_object: "",
    namedObjectMode: NAMED_OBJECT_MODE_CANONICAL,
    structuredIdentityDirty: true,
    legacyAddressResolved: true,
    legacyCompatibilityCopy: false,
    legacyUnresolvedAddress: "",
  };
  if (switchedFromManual || previousId !== nextId) {
    next.building_number = "";
  }
  return next;
}

export function applyManualAddressObjectChange(location, text) {
  const previous = trimText(location.manual_address_object);
  const nextTrim = trimText(text);
  const switchedFromCanonical =
    location.namedObjectMode !== NAMED_OBJECT_MODE_MANUAL;
  const next = {
    ...location,
    manual_address_object: text,
    address_object: null,
    namedObjectMode: NAMED_OBJECT_MODE_MANUAL,
    structuredIdentityDirty: true,
    legacyAddressResolved: nextTrim !== "" ? true : location.legacyAddressResolved,
    legacyCompatibilityCopy: false,
  };
  if (nextTrim !== "") {
    next.legacyUnresolvedAddress = "";
  }
  if (switchedFromCanonical || previous !== nextTrim) {
    next.building_number = "";
  }
  return next;
}

export function switchToManualNamedObject(location) {
  if (location.namedObjectMode === NAMED_OBJECT_MODE_MANUAL) {
    return location;
  }
  return {
    ...location,
    namedObjectMode: NAMED_OBJECT_MODE_MANUAL,
    address_object: null,
    building_number: "",
    structuredIdentityDirty: true,
  };
}

export function switchToCanonicalNamedObject(location) {
  if (location.namedObjectMode === NAMED_OBJECT_MODE_CANONICAL) {
    return location;
  }
  return {
    ...location,
    namedObjectMode: NAMED_OBJECT_MODE_CANONICAL,
    manual_address_object: "",
    building_number: "",
    structuredIdentityDirty: true,
  };
}

export function applyBuildingNumberChange(location, buildingNumber) {
  return {
    ...location,
    building_number: buildingNumber,
    structuredIdentityDirty: true,
  };
}

export function clearLegacyAddressFact(location) {
  return {
    ...location,
    legacyUnresolvedAddress: "",
    legacyAddressResolved: true,
    legacyCompatibilityCopy: false,
    structuredIdentityDirty: true,
  };
}

export function formatSettlementContext(unit) {
  if (!unit) return "";
  const path = Array.isArray(unit.path) ? unit.path : [];
  const ancestorNames = path
    .filter((item) => item.code !== unit.code)
    .map((item) => item.name);
  const categoryPart = unit.category_label || "";
  if (ancestorNames.length === 0) return categoryPart;
  return `${categoryPart} · ${ancestorNames.join(" / ")}`;
}

export function settlementToComboboxOption(unit) {
  return {
    id: unit.code,
    label: unit.name,
    sublabel: formatSettlementContext(unit),
    raw: unit,
  };
}

export function addressObjectKindLabel(kind) {
  return ADDRESS_OBJECT_KIND_LABELS[kind] || ADDRESS_OBJECT_KIND_LABELS.OTHER;
}

export function addressObjectToComboboxOption(addressObject) {
  return {
    id: addressObject.id,
    label: addressObject.name,
    sublabel: addressObjectKindLabel(addressObject.kind),
    raw: addressObject,
  };
}

export function locationCanonicalReady(location) {
  if (!location?.selectedRegion) return false;
  if (location.selectedRegion.category === "K") return true;
  return Boolean(location.selectedSettlement?.code);
}

export function validateLocationCanonical(location) {
  const errors = {};
  if (!location?.selectedRegion) {
    errors.canonical_region =
      "Оберіть область або місто зі спеціальним статусом.";
    return errors;
  }
  if (
    location.selectedRegion.category === "O"
    && !location.selectedSettlement?.code
  ) {
    errors.canonical_settlement = "Оберіть населений пункт.";
  }
  return errors;
}

export function validateLocationStructured(location) {
  const errors = {};
  if (hasInactiveLinkedAddressObject(location)) {
    errors.address_object_id =
      "Оберіть активний адресний об’єкт або введіть назву вручну.";
  }
  if (
    hasUnresolvedLegacyAddress(location)
    && location.structuredIdentityDirty
  ) {
    errors.legacy_address =
      "Уточніть або очистіть попередню адресу перед збереженням змін локації.";
  }
  return errors;
}

export function validateLocationCoordinates(location) {
  const errors = {};
  const num = (value) => (
    value === "" || value === null || value === undefined
      ? null
      : Number(value)
  );
  const latitude = num(location?.latitude);
  if (
    latitude !== null
    && Number.isFinite(latitude)
    && (latitude < -90 || latitude > 90)
  ) {
    errors.latitude = "Широта має бути в межах від -90 до 90.";
  }
  const longitude = num(location?.longitude);
  if (
    longitude !== null
    && Number.isFinite(longitude)
    && (longitude < -180 || longitude > 180)
  ) {
    errors.longitude = "Довгота має бути в межах від -180 до 180.";
  }
  return errors;
}

function shouldOmitStructuredIdentityKeys(location) {
  if (!hasPersistedLocation(location)) return false;
  if (location.structuredIdentityDirty) return false;
  if (location.canonicalSelectionDirty) return false;
  return true;
}

export function buildLocationApiPayload(location, coerceNullable) {
  const payload = {
    latitude: coerceNullable(location.latitude),
    longitude: coerceNullable(location.longitude),
  };

  if (location.canonicalSelectionDirty && location.selectedSettlement?.code) {
    payload.settlement_code = location.selectedSettlement.code;
  }

  if (shouldOmitStructuredIdentityKeys(location)) {
    return payload;
  }

  payload.local_area = trimText(location.local_area);
  if (location.namedObjectMode === NAMED_OBJECT_MODE_MANUAL) {
    payload.address_object_id = null;
    payload.manual_address_object = trimText(location.manual_address_object);
  } else {
    payload.address_object_id = addressObjectIdOf(location.address_object);
    payload.manual_address_object = "";
  }
  payload.building_number = trimText(location.building_number);
  return payload;
}

export function shouldValidateCanonicalOnEdit(location) {
  return Boolean(location?.canonicalSelectionDirty);
}
